import React, { useCallback, useEffect, useRef, useState } from 'react';
import adminAxios from '../../interceptors/adminAxios';

interface HbSettings {
  heartbeat_active_sec: number;
  heartbeat_passive_sec: number;
  heartbeat_active_window_min: number;
}

interface CacheSettings { default_cache_ttl_days: number; }
interface OfflineSettings { offline_timeout_days: number; }

const field: React.CSSProperties = {
  width: '100%', boxSizing: 'border-box', padding: '8px 12px',
  background: 'var(--input-bg)', border: '1px solid var(--input-border)',
  borderRadius: 8, color: 'var(--text-primary)', fontSize: 13, fontFamily: 'inherit',
  outline: 'none', transition: 'border-color .15s',
};

const AdminPortalSettingsPage: React.FC = () => {
  const [hb, setHb]           = useState<HbSettings>({ heartbeat_active_sec: 30, heartbeat_passive_sec: 120, heartbeat_active_window_min: 30 });
  const [cache, setCache]     = useState<CacheSettings>({ default_cache_ttl_days: 30 });
  const [offline, setOffline] = useState<OfflineSettings>({ offline_timeout_days: 7 });
  const [dirty, setDirty]     = useState(false);
  const [saving, setSaving]   = useState(false);
  const [saved, setSaved]     = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const load = useCallback(async () => {
    try {
      const [hbRes, cacheRes, offlineRes] = await Promise.all([
        adminAxios.get<HbSettings>('/admin/portal-settings/heartbeat'),
        adminAxios.get<CacheSettings>('/admin/portal-settings/cache'),
        adminAxios.get<OfflineSettings>('/admin/portal-settings/offline'),
      ]);
      setHb(hbRes.data);
      setCache(cacheRes.data);
      setOffline(offlineRes.data);
      setDirty(false);
    } catch {}
  }, []);

  useEffect(() => { load(); }, [load]);

  const updateHb = (patch: Partial<HbSettings>) => { setHb(prev => ({ ...prev, ...patch })); setDirty(true); setSaved(false); };
  const updateCache = (patch: Partial<CacheSettings>) => { setCache(prev => ({ ...prev, ...patch })); setDirty(true); setSaved(false); };
  const updateOffline = (patch: Partial<OfflineSettings>) => { setOffline(prev => ({ ...prev, ...patch })); setDirty(true); setSaved(false); };

  const save = async () => {
    setSaving(true);
    try {
      await Promise.all([
        adminAxios.patch('/admin/portal-settings/heartbeat', hb),
        adminAxios.patch('/admin/portal-settings/cache', cache),
        adminAxios.patch('/admin/portal-settings/offline', offline),
      ]);
      setDirty(false);
      setSaved(true);
      if (timer.current) clearTimeout(timer.current);
      timer.current = setTimeout(() => setSaved(false), 2000);
    } catch {}
    setSaving(false);
  };

  const cardSt: React.CSSProperties = {
    background: 'var(--glass-bg)', border: '1px solid var(--glass-border)',
    borderRadius: 14, padding: '20px 24px', marginBottom: 16,
  };

  const labelSt: React.CSSProperties = {
    fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.08em',
    color: 'var(--text-disabled)', marginBottom: 8, display: 'block',
  };

  const hintSt: React.CSSProperties = {
    fontSize: 11, color: 'var(--text-disabled)', marginTop: 4,
  };

  return (
    <div style={{ flex: 1, overflowY: 'auto', padding: '20px 24px' }}>

      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24 }}>
        <div>
          <div style={{ fontSize: 18, fontWeight: 700, color: 'var(--text-primary)' }}>Portal Settings</div>
          <div style={{ fontSize: 12, color: 'var(--text-disabled)', marginTop: 3 }}>
            Default values applied to all companies and new devices
          </div>
        </div>
        <button
          onClick={save}
          disabled={!dirty || saving}
          style={{
            padding: '8px 20px', borderRadius: 10, border: 'none', fontFamily: 'inherit',
            background: saved ? 'rgba(16,185,129,.85)' : 'var(--accent)',
            color: '#fff', fontSize: 13, fontWeight: 600,
            cursor: (!dirty || saving) ? 'not-allowed' : 'pointer',
            opacity: (!dirty || saving) ? 0.5 : 1, transition: 'background .2s',
          }}
        >
          {saved ? '✓ Saved' : saving ? 'Saving…' : 'Save'}
        </button>
      </div>

      {/* Heartbeat section */}
      <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.08em', color: 'var(--text-disabled)', marginBottom: 10 }}>
        Device Heartbeat
      </div>
      <div style={cardSt}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 16, marginBottom: 16 }}>

          {/* Active interval */}
          <div>
            <label style={labelSt}>Active interval (sec)</label>
            <input
              type="number" min={1} max={3600}
              value={hb.heartbeat_active_sec}
              onChange={e => updateHb({ heartbeat_active_sec: Math.max(1, +e.target.value) })}
              style={field}
            />
            <div style={hintSt}>When screen is active — ping every N seconds</div>
          </div>

          {/* Passive interval */}
          <div>
            <label style={labelSt}>Passive interval (sec)</label>
            <input
              type="number" min={1} max={86400}
              value={hb.heartbeat_passive_sec}
              onChange={e => updateHb({ heartbeat_passive_sec: Math.max(1, +e.target.value) })}
              style={field}
            />
            <div style={hintSt}>When screen is idle — ping every N seconds</div>
          </div>

          {/* Active window */}
          <div>
            <label style={labelSt}>Active window (min)</label>
            <input
              type="number" min={1} max={1440}
              value={hb.heartbeat_active_window_min}
              onChange={e => updateHb({ heartbeat_active_window_min: Math.max(1, +e.target.value) })}
              style={field}
            />
            <div style={hintSt}>After last request → switch to passive mode</div>
          </div>

        </div>

        <div style={{ fontSize: 11, color: 'var(--text-disabled)', paddingTop: 12, borderTop: '1px solid var(--glass-border)' }}>
          These are portal-wide defaults. Per-company overrides will be configurable in the company settings (coming later).
          Changes take effect for new devices on next registration.
        </div>
      </div>

      {/* Cache TTL section */}
      <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.08em', color: 'var(--text-disabled)', marginBottom: 10 }}>
        Cache TTL (days)
      </div>
      <div style={cardSt}>
        <div style={{ display: 'grid', gridTemplateColumns: '200px 1fr', gap: 16, alignItems: 'start' }}>
          <div>
            <label style={labelSt}>Default cache TTL (days)</label>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <input
                type="number" min={0} max={365}
                value={cache.default_cache_ttl_days}
                onChange={e => updateCache({ default_cache_ttl_days: Math.max(0, +e.target.value) })}
                style={{ ...field, width: 80, textAlign: 'center' }}
              />
              <span style={{ fontSize: 13, color: 'var(--text-secondary)' }}>
                {cache.default_cache_ttl_days === 0 ? 'unlimited (keep forever)' : 'days'}
              </span>
            </div>
          </div>
          <div style={{ ...hintSt, paddingTop: 26 }}>
            Delete unused files from the device after N days. 0 = keep forever. Applied to new devices on activation; existing devices retain their own value.
          </div>
        </div>
      </div>

      {/* Offline Blocking section */}
      <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.08em', color: 'var(--text-disabled)', marginBottom: 10 }}>
        Offline blocking (days)
      </div>
      <div style={cardSt}>
        <div style={{ display: 'grid', gridTemplateColumns: '200px 1fr', gap: 16, alignItems: 'start' }}>
          <div>
            <label style={labelSt}>Max offline period (days)</label>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <input
                type="number" min={0} max={365}
                value={offline.offline_timeout_days}
                onChange={e => updateOffline({ offline_timeout_days: Math.max(0, +e.target.value) })}
                style={{ ...field, width: 80, textAlign: 'center' }}
              />
              <span style={{ fontSize: 13, color: 'var(--text-secondary)' }}>
                {offline.offline_timeout_days === 0 ? 'unlimited' : 'days'}
              </span>
            </div>
          </div>
          <div style={{ ...hintSt, paddingTop: 26 }}>
            Stop playback and show "Connection lost" if the device has not connected to the server for N days. 0 = no limit. Applied to new devices on activation; existing devices retain their own value.
          </div>
        </div>
      </div>

    </div>
  );
};

export default AdminPortalSettingsPage;
